<script lang="ts">
  import type { DisplayView, PublicPlayer } from '@banger/shared';
  import Avatar from '../shared/Avatar.svelte';

  let { view, secs, byId, steals }: {
    view: DisplayView; secs: number; byId: Map<string, PublicPlayer>; steals: string[];
  } = $props();

  let artFailed = $state(false);
  const r = $derived(view.reveal);
  const results = $derived(
    (r?.results ?? []).flatMap((x) => {
      const p = byId.get(x.playerId);
      return p ? [{ ...x, p }] : [];
    }),
  );
</script>

<main>
  {#if r}
    <section class="song">
      <div class="art">
        {#if r.artUrl && !artFailed}
          <img src={r.artUrl} alt="Album art for {r.title}" onerror={() => (artFailed = true)} />
        {:else}
          <div class="vinyl" aria-hidden="true"><div class="label">B!</div></div>
        {/if}
      </div>
      <div class="info">
        <p class="kicker">That was…</p>
        <h1 class="title">{r.title}</h1>
        <p class="artist">{r.artist}</p>
        <p class="year neon">{r.year}</p>
      </div>
    </section>

    <ul class="results">
      {#each results as x (x.playerId)}
        <li class:right={x.correct}>
          <Avatar emoji={x.p.emoji} size={64} />
          <span class="name">{x.p.name}</span>
          <span class="mark">{x.correct ? '✅' : '❌'}</span>
          {#if x.exact}<span class="exact">🎯 Exact! Steal!</span>{/if}
        </li>
      {/each}
    </ul>

    {#each steals as line}<p class="steal">{line}</p>{/each}
    <p class="hint">Mark your square on your phone · {secs}</p>
  {/if}
</main>

<style>
  main { min-height: 100%; display: grid; align-content: center; justify-items: center; gap: 3vh; padding: 4vh 4vw; text-align: center; }
  .song { display: flex; align-items: center; gap: 4vw; text-align: left; animation: in 0.35s ease-out both; }
  .art { width: min(36vh, 30vw); aspect-ratio: 1; flex: none; }
  .art img { width: 100%; height: 100%; object-fit: cover; border-radius: 12px; box-shadow: 0 0 40px rgba(255, 61, 242, 0.6), 0 0 90px rgba(125, 249, 255, 0.3); }
  .vinyl {
    width: 100%; height: 100%; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    background:
      radial-gradient(circle, transparent 0 31%, rgba(255, 255, 255, 0.07) 31.5% 32%, transparent 32.5%),
      radial-gradient(circle at 30% 25%, rgba(255, 255, 255, 0.22), transparent 45%),
      repeating-radial-gradient(circle, #24203a 0 3px, #3a3456 3px 5px),
      #24203a;
    box-shadow: 0 0 0 4px var(--pink), 0 0 30px var(--pink), 0 0 70px var(--pink), 0 0 110px rgba(125, 249, 255, 0.55);
    animation: turn 4s linear infinite;
  }
  .label {
    width: 34%; aspect-ratio: 1; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    background: radial-gradient(circle, #0a0618 0 8%, #ff6ff5 9%, var(--pink) 70%); color: #fff; font-weight: 900;
    font-size: clamp(1rem, 2.6vh, 2rem); text-shadow: 0 0 8px var(--cyan), 0 0 2px #0a0618;
    box-shadow: 0 0 24px var(--pink), inset 0 0 12px rgba(255, 255, 255, 0.35);
  }
  .info { display: grid; gap: 0.6vh; min-width: 0; }
  .kicker { margin: 0; font-size: clamp(1.2rem, 2.2vw, 2rem); font-weight: 800; color: #fff; text-transform: uppercase; letter-spacing: 0.2em; text-shadow: 0 0 10px rgba(255, 61, 242, 0.8); }
  .title { margin: 0; font-size: clamp(2.6rem, 6vw, 6rem); font-weight: 900; line-height: 1.02; max-width: 16ch; color: #fff; text-shadow: 0 0 12px rgba(255, 255, 255, 0.55), 0 0 32px rgba(255, 255, 255, 0.25); }
  .artist { margin: 0; font-size: clamp(1.8rem, 3.6vw, 3.4rem); color: #7df9ff; font-weight: 800; text-shadow: 0 0 10px #7df9ff, 0 0 28px rgba(125, 249, 255, 0.7); }
  /* The hottest thing on screen. */
  .year {
    margin: 0; font-size: clamp(3rem, 8vw, 7.5rem); font-weight: 900; line-height: 1; color: #ff3df2;
    text-shadow: 0 0 6px #ff3df2, 0 0 18px #ff3df2, 0 0 40px #ff3df2, 0 0 80px #ff3df2;
  }
  .results { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; justify-content: center; gap: 1.2vw 2vw; }
  li {
    display: flex; align-items: center; gap: 10px; padding: 6px 16px 6px 6px; border-radius: 999px;
    background: rgba(255, 255, 255, 0.1); border: 2px solid rgba(255, 255, 255, 0.3);
    animation: in 0.35s ease-out both;
  }
  li:not(.right) { opacity: 0.88; }
  li.right { border-color: #69db7c; box-shadow: 0 0 16px rgba(105, 219, 124, 0.45); }
  .name { font-size: clamp(1.2rem, 2vw, 1.8rem); font-weight: 800; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mark { font-size: clamp(1.4rem, 2.2vw, 2rem); }
  .exact { font-weight: 900; color: var(--pink); text-shadow: 0 0 10px var(--pink); font-size: clamp(1rem, 1.6vw, 1.4rem); }
  .steal { margin: 0; font-size: clamp(1.4rem, 2.6vw, 2.4rem); font-weight: 800; }
  .hint { margin: 0; font-size: clamp(1.1rem, 2vw, 1.8rem); opacity: 0.85; }
  @keyframes in { from { transform: translateY(24px); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes turn { to { transform: rotate(360deg); } }
</style>
